export interface MenuItem {
  allowedRoles?: string[];
  requiresOrganizationId?: boolean;
  hidden?: boolean;
  menuItems?: MenuItem[];
  [key: string]: any;
}

// Fonction pour créer une copie profonde des menus
function deepCopyMenu(menu: MenuItem[]): MenuItem[] {
  return menu.map((item) => ({
    ...item,
    allowedRoles: item.allowedRoles ? [...item.allowedRoles] : undefined,
    menuItems: item.menuItems ? deepCopyMenu(item.menuItems) : undefined,
  }));
}

/** BL-0339 — items flaggés `requiresOrganizationId` exclus sans org de session. */
function menuItemAllowedByOrganization(item: MenuItem, hasOrganizationId: boolean): boolean {
  if (!item.requiresOrganizationId) {
    return true;
  }
  return hasOrganizationId;
}

export const updateMenu = (
  menu: MenuItem[],
  securityTags: string[],
  organizationId?: string | null,
): MenuItem[] => {
  // Créer une copie profonde pour éviter les mutations du menu original
  const menuCopy = deepCopyMenu(menu);
  const hasOrganizationId = Boolean(organizationId);

  function filterBySecurityTags(items: MenuItem[]): MenuItem[] {
    return items.reduce((filtered: MenuItem[], item: MenuItem) => {
      // Créer une copie de l'item pour éviter la mutation
      const itemCopy = { ...item };

      // Traitement récursif des sous-menus
      if (itemCopy.menuItems) {
        const filteredSubMenus = filterBySecurityTags(itemCopy.menuItems);
        if (filteredSubMenus.length > 0) {
          itemCopy.menuItems = filteredSubMenus;
          filtered.push(itemCopy);
        }
        // Si pas de sous-menus valides, on n'ajoute pas l'item parent
      } else {
        // Item terminal - vérifier les rôles autorisés
        if (itemCopy.allowedRoles && itemCopy.allowedRoles.length > 0) {
          const hasValidRole = itemCopy.allowedRoles.some((role: string) => securityTags.includes(role));
          if (hasValidRole && menuItemAllowedByOrganization(itemCopy, hasOrganizationId)) {
            filtered.push(itemCopy);
          }
        } else if (menuItemAllowedByOrganization(itemCopy, hasOrganizationId)) {
          // Pas de restriction de rôle, ajouter l'item
          filtered.push(itemCopy);
        }
      }

      return filtered;
    }, []);
  }

  return filterBySecurityTags(menuCopy);
};

/** Path de navigation d'un item (menu catalogue = `link`, favoris = `path`). */
function getMenuItemPath(item: MenuItem): string | null {
  const raw = item.link ?? item.path;
  if (typeof raw === "string" && raw.length > 0) {
    return raw;
  }
  return null;
}

/**
 * Collecte les paths feuilles du menu (ordre de parcours profondeur-gauche).
 * Un item est une feuille s'il n'a pas de sous-menus non vides.
 */
export function collectLeafMenuPaths(menu: MenuItem[]): string[] {
  const paths: string[] = [];

  function walk(items: MenuItem[]): void {
    for (const item of items) {
      if (item.menuItems && item.menuItems.length > 0) {
        walk(item.menuItems);
      } else {
        const path = getMenuItemPath(item);
        if (path) {
          paths.push(path);
        }
      }
    }
  }

  walk(menu);
  return paths;
}

/** Premier path feuille du menu filtré (ordre catalogue, profondeur-gauche). */
export function findFirstLeafMenuPath(menu: MenuItem[]): string | null {
  for (const item of menu) {
    if (item.menuItems && item.menuItems.length > 0) {
      const nested = findFirstLeafMenuPath(item.menuItems);
      if (nested) {
        return nested;
      }
    } else {
      const path = getMenuItemPath(item);
      if (path) {
        return path;
      }
    }
  }
  return null;
}

/** True si `path` correspond à une feuille du menu (déjà filtré par securityTags). */
export function isMenuPathAllowed(menu: MenuItem[], path: string): boolean {
  return collectLeafMenuPaths(menu).includes(path);
}

const U3400_READ = "U3400:READ";
const U2106_READ = "U2106:READ";

export const U2000_NO_ALLOWED_SCREEN_PATH = "/unauthorized";

export interface ResolveU2000DefaultRouteParams {
  menu: MenuItem[];
  securityTags: string[];
  defaultScreen: string | null | undefined;
  fallbackPath?: string;
}

/**
 * Résout la route post-login / post-changement d’org (BL-0332).
 * Ordre : favori autorisé → /U3400 si U3400:READ → premier path menu filtré → /U2106/profile → /unauthorized.
 */
export function resolveU2000DefaultRoute({
  menu,
  securityTags,
  defaultScreen,
  fallbackPath = "/U3400",
}: ResolveU2000DefaultRouteParams): string {
  if (defaultScreen && isMenuPathAllowed(menu, defaultScreen)) {
    return defaultScreen;
  }

  if (securityTags.includes(U3400_READ)) {
    return fallbackPath;
  }

  const firstAllowed = findFirstLeafMenuPath(menu);
  if (firstAllowed) {
    return firstAllowed;
  }

  if (securityTags.includes(U2106_READ)) {
    return "/U2106/profile";
  }

  return U2000_NO_ALLOWED_SCREEN_PATH;
}

type CleanupType = "USER_CHANGE" | "ORGANIZATION_INVALID";

// Fonction utilitaire pour nettoyer les clés localStorage d'un ancien utilisateur ou organisations invalides
export const cleanupOldUserStorage = (currentUserKey: string, cleanupType: CleanupType = "USER_CHANGE"): void => {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;

      const isMenuOrFavorite = key.includes("MenuState") || key.includes("FavoriteMenu");
      const isOrganizationPersistence = key.includes("U2001.lastOrganization");
      const containsCurrent = key.includes(currentUserKey);

      if (cleanupType === "USER_CHANGE") {
        // Existing logic - clean all old user data including organization persistence
        if ((isMenuOrFavorite || isOrganizationPersistence) && !containsCurrent) {
          keysToRemove.push(key);
        }
      } else if (cleanupType === "ORGANIZATION_INVALID") {
        // New logic - clean invalid organization persistence for specific user
        if (isOrganizationPersistence && key.includes(currentUserKey.split("_")[0])) {
          keysToRemove.push(key);
        }
      }
    }

    if (keysToRemove.length > 0) {
      keysToRemove.forEach((key) => {
        try {
          localStorage.removeItem(key);
        } catch (err) {
          console.warn("cleanupOldUserStorage - failed to remove key:", key, err);
        }
      });
    }
  } catch (error) {
    console.warn("Erreur lors du nettoyage du localStorage:", error);
  }
};

// Fonction pour nettoyer la persistance d'organisation invalide pour un utilisateur spécifique
export const clearInvalidOrganizationPersistence = (userName: string): void => {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;

      // Remove organization persistence keys for this user (any organization)
      if (key.includes("U2001.lastOrganization") && key.includes(`${userName}_`)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => {
      try {
        localStorage.removeItem(key);
      } catch (err) {
        console.warn("clearInvalidOrganizationPersistence - failed to remove key:", key, err);
      }
    });
  } catch (error) {
    console.warn("Erreur lors du nettoyage de la persistance d'organisation:", error);
  }
};
